import { RenewalGroup } from './renewalGroups';
import { PackageResult } from './types';

/** The pricing engine's multi-subject discount; the API does not expose the figure. */
const MULTI_SUBJECT_DISCOUNT_PERCENT = 10;

/** A subscription with a renewal panel, and what that panel last priced it at. */
export type DiscountCandidate = {
  subjectsId: number;
  /** The renewal group, i.e. the payment, the subject belongs to. */
  groupKey: string;
  pkg?: PackageResult;
};

/** groupKey -> the packages that renewal group's summary last priced. */
export type PricedPackagesByGroup = { [groupKey: string]: PackageResult[] };

export type MissedDiscountReasons = {
  /** Package type or length differs between the subjects. */
  selection: boolean;
  /** Auto-renew differs, so the subjects are paid for separately. */
  autoRenew: boolean;
};

export type DiscountStatus = {
  applied: boolean;
  /** Why it is missing, when the school's own choices on this page are the cause. */
  lostTo: MissedDiscountReasons | null;
};

export const discountCandidates = (groups: RenewalGroup[], priced: PricedPackagesByGroup): DiscountCandidate[] =>
  groups.reduce(
    (all: DiscountCandidate[], group) =>
      all.concat(
        group.subjects.map((s) => ({
          subjectsId: s.subjectsId,
          groupKey: group.key,
          pkg: (priced[group.key] || []).find((p) => p.subjectsID === s.subjectsId),
        }))
      ),
    []
  );

/** A panel that has gone would otherwise leave its last price behind to be read as current. */
export const keepPricedGroups = (priced: PricedPackagesByGroup, groupKeys: string[]): PricedPackagesByGroup => {
  const kept: PricedPackagesByGroup = {};
  Object.keys(priced)
    .filter((key) => groupKeys.indexOf(key) !== -1)
    .forEach((key) => {
      kept[key] = priced[key];
    });

  return Object.keys(kept).length === Object.keys(priced).length ? priced : kept;
};

const distinctCount = (values: (number | string)[]) => values.filter((v, i) => values.indexOf(v) === i).length;

// Half a penny of slack, so a saving of exactly the discount is not read as less.
const savesLessThanMultiSubject = (pkg: PackageResult) =>
  pkg.netSaving + 0.005 < (pkg.netUndiscounted * MULTI_SUBJECT_DISCOUNT_PERCENT) / 100;

/**
 * Whether the multi-subject discount is applied across the renewal panels, or null while any of
 * them is still to price. The engine needs every subject in one payment with the same package
 * type and length, so those are the reasons reported. A promo code saving as much on every
 * subject is not a loss.
 */
export const discountStatus = (candidates: DiscountCandidate[]): DiscountStatus | null => {
  const packages = candidates.map((c) => c.pkg).filter((p): p is PackageResult => !!p);
  if (candidates.length === 0 || packages.length !== candidates.length) return null;

  if (packages.some((p) => !!p.multiPurchaseDiscountApplied)) {
    return { applied: true, lostTo: null };
  }

  const reasons: MissedDiscountReasons = {
    selection:
      distinctCount(packages.map((p) => p.generalPackagesID)) > 1 ||
      distinctCount(packages.map((p) => p.subscriptionTypesID)) > 1,
    autoRenew: distinctCount(candidates.map((c) => c.groupKey)) > 1,
  };
  const reported = (reasons.selection || reasons.autoRenew) && packages.some(savesLessThanMultiSubject);

  return { applied: false, lostTo: reported ? reasons : null };
};
